import { CollectionConfig } from "@rebasepro/types";

/**
 * Prospect Contacts — the people at an employer we intend to write to.
 *
 * `company_prospects` had `contact_name` and `contact_email`, which says an
 * employer has exactly one contact. Real ones have several and of different
 * kinds: the mailbox the posting publishes, the head of sustainability who
 * feels the vacancy, whoever runs HR. Each is a row here, keyed on the same
 * name-derived `employer_key` as the prospect — no foreign key is possible,
 * for the reason `company_prospects` documents at length.
 *
 * ── About the addresses ──────────────────────────────────────────────
 *
 * There is no verification vendor in this product and `email_status` has no
 * `verified` value, deliberately. An address here was published somewhere we
 * read, typed by a person, or derived from a name and a domain — and the last
 * of those is a guess that is labelled as one everywhere it is shown.
 * `email_alternatives` holds the other spellings the same name could produce,
 * because a Spanish name has two surnames and the mail server picked one.
 *
 * Admin-only and FORCE RLS in the database: every row names a real person at a
 * company that has not asked us for anything.
 */
const prospectContactsCollection: CollectionConfig = {
    name: "Personas de contacto",
    singularName: "Persona de contacto",
    slug: "prospect-contacts",
    table: "prospect_contacts",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        employer_key: {
            name: "Clave de empresa",
            type: "string",
            validation: { required: true },
            description: "La misma identidad normalizada que usa la ficha de la empresa (config/employer_identity.ts).",
            admin: { readOnly: true }
        },
        name_key: {
            name: "Clave de la persona",
            type: "string",
            validation: { required: true },
            description: "Identidad para deduplicar dentro de la empresa. Se deriva del nombre: no editar.",
            admin: { readOnly: true }
        },
        kind: {
            name: "Tipo",
            type: "string",
            enum: [
                { id: "person", label: "Persona", color: "blue" },
                // A shared mailbox is a real channel with nobody's name on it.
                // Kept distinct so nothing ever addresses an email to "rrhh@".
                { id: "mailbox", label: "Buzón", color: "gray" }
            ],
            defaultValue: "person"
        },
        full_name: {
            name: "Nombre",
            type: "string",
            description: "Vacío en un buzón compartido, y en un perfil de LinkedIn del que sólo conocemos la URL."
        },
        role_title: {
            name: "Cargo",
            type: "string",
            description: "Tal como lo escribe la web de la empresa o la oferta"
        },
        role_bucket: {
            name: "Función",
            type: "string",
            enum: [
                // The order is the order the screen shows them in, which is the
                // order they matter for a placement pitch.
                { id: "talent", label: "Talento / RRHH", color: "green" },
                { id: "sustainability", label: "Sostenibilidad", color: "cyan" },
                { id: "leadership", label: "Dirección", color: "purple" },
                { id: "other", label: "Otro", color: "gray" }
            ],
            defaultValue: "other"
        },
        email: {
            name: "Email",
            type: "string"
        },
        email_status: {
            name: "Origen del email",
            type: "string",
            enum: [
                { id: "none", label: "Sin email", color: "gray" },
                { id: "pattern_guess", label: "La dedujimos del nombre y el dominio", color: "orange" },
                { id: "published", label: "Publicado", color: "blue" },
                { id: "manual", label: "Introducido a mano", color: "green" }
                // No "verificado": nada en esta aplicación puede comprobarlo.
            ],
            defaultValue: "none"
        },
        email_alternatives: {
            name: "Otros patrones",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Dirección" },
            description: "Direcciones igual de probables para el mismo nombre. Nunca son 'el email', son la segunda opción."
        },
        linkedin_url: {
            name: "LinkedIn",
            type: "string"
        },
        phone: {
            name: "Teléfono",
            type: "string"
        },
        source: {
            name: "Procedencia",
            type: "string",
            enum: [
                { id: "job_posting", label: "Oferta", color: "blue" },
                { id: "website", label: "Web de la empresa", color: "cyan" },
                { id: "manual", label: "Añadido a mano", color: "green" }
            ],
            defaultValue: "manual"
        },
        source_url: {
            name: "Fuente",
            type: "string",
            description: "La página o la oferta donde apareció"
        },
        notes: {
            name: "Notas",
            type: "string",
            admin: { multiline: true }
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizado",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Captación",
        icon: "Contact",
        // Not in `navigationGroupMappings`, like `company_prospects` itself:
        // these rows are meant to be used from "Empresas que contratan", where
        // they sit next to the employer that gives them their meaning.
        sort: ["updated_at", "desc"],
        // Fuera del menú: esta tabla es el interior de "Empresas que contratan",
        // no una pantalla. Como entrada del menú era tres cosas confusas a la
        // vez — vacía hasta que alguien actúa, sin explicar qué es, y un
        // segundo camino peor hacia esa pantalla.
        //
        // `group` por sí solo NO la ocultaba: el menú muestra cualquier
        // colección que declare uno, esté o no en `navigationGroupMappings`.
        // Sigue siendo navegable — el lápiz de la pantalla abre la fila.
        hideFromNavigation: true
    }
};

export default prospectContactsCollection;
